import React, { useEffect, useState } from "react";
import { Monitor, Smartphone, MessageCircle, MessageSquare, Sparkles, RotateCcw, AlertCircle } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { WIDGET_ORIGIN } from "../../utils/widgetOriginConfig";
import { useWidgetPreview } from "../../hooks/useWidgetPreview";

// Mirror of MAX_INVITE_TEXT_LENGTH in @ai-agent-widget/shared/src/messages.ts.
// The loader drops an invite whose text is longer, so a sample the field let
// someone type past this would preview a card production would never show.
const MAX_INVITE_TEXT_LENGTH = 120;

const STATE_TABS = [
  { id: "launcher", icon: MessageCircle, labelKey: "previewLauncher", labelFallback: "Launcher" },
  { id: "welcome", icon: Sparkles, labelKey: "previewWelcome", labelFallback: "Welcome" },
  { id: "panel", icon: MessageSquare, labelKey: "previewPanel", labelFallback: "Panel" },
];

const DEVICE_TABS = [
  { id: "desktop", icon: Monitor, labelKey: "previewDesktop", labelFallback: "Desktop" },
  { id: "mobile", icon: Smartphone, labelKey: "previewMobile", labelFallback: "Mobile" },
];

// Launcher is a small bottom-right FAB; rendering it inside a 720px-tall canvas
// leaves the top 600px empty and looks broken.
const STATE_MIN_HEIGHT = { launcher: 360, welcome: 720, panel: 720 };

// The invite card's sample text is typed here and nowhere else. It rides the
// state channel to the widget, which sends it immediately, so it is debounced
// here: a keystroke that reached the card would restart its reveal on every
// letter and the message would never finish writing itself in.
const INVITE_DEBOUNCE_MS = 250;

const DEVICE_FRAME = {
  desktop: { width: "100%", maxWidth: "none" },
  mobile: { width: 390, maxWidth: 390 },
};

const PreviewPane = ({ config }) => {
  const { translate } = useLanguage();
  const [previewState, setPreviewState] = useState("panel");
  const [device, setDevice] = useState("desktop");
  const sampleInvite = translate("previewInviteSample") || "Can I help with anything?";
  const [showInvite, setShowInvite] = useState(false);
  const [inviteText, setInviteText] = useState(sampleInvite);
  const [debouncedInvite, setDebouncedInvite] = useState(sampleInvite);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedInvite(inviteText), INVITE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [inviteText]);

  // Only on the launcher tab: the card exists beside the launcher and nowhere
  // else, so leaving it armed behind the panel would send a card nobody can
  // see and replay its reveal on every state click.
  //
  // Trimmed because production trims (readHostInvite, in the widget's loader)
  // and drops what is left empty. Sent raw, a field holding only spaces is
  // truthy, survives `|| undefined`, and the preview draws a blank card that
  // no host could ever put on screen.
  const invite = previewState === "launcher" && showInvite ? debouncedInvite.trim() : "";
  const { iframeRef, reloadKey, error, reload } = useWidgetPreview({ config, previewState, invite });

  const canvasHeight = STATE_MIN_HEIGHT[previewState] || 720;
  const frame = DEVICE_FRAME[device];

  return (
    <div className="rounded-xl overflow-hidden"
      style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)" }}>
      <div className="flex items-center justify-between px-4 py-3 gap-2 flex-wrap"
        style={{ borderBottom: "1px solid var(--color-border)" }}>
        <div className="inline-flex rounded-lg" style={{ background: "var(--color-surface-raised)", padding: "2px" }}>
          {STATE_TABS.map(({ id, icon: Icon, labelKey, labelFallback }) => (
            <button
              key={id}
              type="button"
              onClick={() => setPreviewState(id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
              style={{
                backgroundColor: previewState === id ? "var(--color-surface)" : "transparent",
                color: previewState === id ? "var(--color-text-primary)" : "var(--color-text-muted)",
              }}
            >
              <Icon size={14} />
              {translate(labelKey) || labelFallback}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg" style={{ background: "var(--color-surface-raised)", padding: "2px" }}>
            {DEVICE_TABS.map(({ id, icon: Icon, labelKey, labelFallback }) => (
              <button
                key={id}
                type="button"
                onClick={() => setDevice(id)}
                title={translate(labelKey) || labelFallback}
                aria-label={translate(labelKey) || labelFallback}
                className="flex items-center px-2 py-1.5 rounded-md transition-colors"
                style={{
                  backgroundColor: device === id ? "var(--color-surface)" : "transparent",
                  color: device === id ? "var(--color-text-primary)" : "var(--color-text-muted)",
                }}
              >
                <Icon size={14} />
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={reload}
            title={translate("reloadPreview") || "Reload preview"}
            className="p-1.5 rounded hover:bg-gray-100 transition-colors"
            style={{ color: "var(--color-text-muted)" }}
          >
            <RotateCcw size={14} />
          </button>
        </div>
      </div>
      {previewState === "launcher" && (
        <div className="px-4 py-3 flex flex-col gap-2" style={{ borderBottom: "1px solid var(--color-border)" }}>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 text-sm font-medium cursor-pointer"
              style={{ color: "var(--color-text-primary)" }}>
              <input
                type="checkbox"
                checked={showInvite}
                onChange={(e) => setShowInvite(e.target.checked)}
              />
              {translate("previewInviteShow") || "Show invite"}
            </label>
            <input
              type="text"
              value={inviteText}
              disabled={!showInvite}
              maxLength={MAX_INVITE_TEXT_LENGTH}
              onChange={(e) => setInviteText(e.target.value)}
              placeholder={translate("previewInvitePlaceholder") || "Invite message"}
              className="flex-1 min-w-0 px-3 py-1.5 rounded-lg text-sm"
              style={{
                border: "1px solid var(--color-border)",
                background: showInvite ? "var(--color-surface)" : "var(--color-surface-raised)",
                color: "var(--color-text-primary)",
                opacity: showInvite ? 1 : 0.6,
              }}
            />
          </div>
          {/* The one thing worth saying out loud here: this is not a setting. */}
          <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
            {translate("previewInviteHint") ||
              "Preview only. The invite text comes from your website, not from these settings."}
          </p>
        </div>
      )}
      <div className="relative flex justify-center"
        style={{ background: "#f5f5f5", minHeight: `${canvasHeight}px`, padding: device === "mobile" ? "16px" : "0" }}>
        {error && (
          <div className="absolute inset-0 z-10 flex items-center justify-center px-6 text-center">
            <div className="max-w-xs">
              <AlertCircle size={32} style={{ color: "var(--color-error)" }} className="mx-auto mb-3" />
              <p className="text-sm font-medium mb-2" style={{ color: "var(--color-error)" }}>
                {translate("previewUnavailable") || "Preview unavailable"}
              </p>
              <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>{error}</p>
              <button
                type="button"
                onClick={reload}
                className="mt-3 text-sm font-medium underline"
                style={{ color: "var(--color-mint)" }}
              >
                {translate("retry") || "Retry"}
              </button>
            </div>
          </div>
        )}
        <iframe
          ref={iframeRef}
          key={reloadKey}
          title="Widget preview"
          src={`${WIDGET_ORIGIN}/widget.html?preview=1`}
          allow={`microphone ${WIDGET_ORIGIN}`}
          style={{
            width: frame.width,
            maxWidth: frame.maxWidth,
            height: `${canvasHeight - (device === "mobile" ? 32 : 0)}px`,
            border: 0,
            borderRadius: device === "mobile" ? "20px" : 0,
            boxShadow: device === "mobile" ? "0 8px 24px rgba(0,0,0,0.12)" : "none",
            background: "transparent",
          }}
        />
      </div>
    </div>
  );
};

export default PreviewPane;
